import React, { useState, useEffect, useCallback } from 'react';
import DatePicker from 'react-datepicker';
import { XCircleIcon, ClockIcon, CalendarIcon, PlusIcon, TrashIcon, CheckCircleIcon } from 'lucide-react';
import { api } from '../../services/api';

// Custom styles for DatePicker
const datePickerStyles = `
  .react-datepicker-wrapper {
    width: 100%;
  }
  .react-datepicker__input-container input {
    width: 100%;
    border: 1px solid #d1d5db;
    border-radius: 0.375rem;
    padding: 0.5rem 0.75rem;
    font-size: 0.875rem;
    color: #374151;
    background-color: #f9fafb;
    transition: all 0.2s;
  }
  .react-datepicker__input-container input:focus {
    outline: none;
    border-color: #06b6d4;
    box-shadow: 0 0 0 2px rgba(6, 182, 212, 0.2);
    background-color: #ffffff;
  }
  .react-datepicker__input-container input::placeholder {
    color: #9ca3af;
  }
  .react-datepicker {
    font-family: inherit;
    border: 1px solid #d1d5db;
    border-radius: 0.5rem;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
    background-color: #ffffff;
  }
  .react-datepicker__header {
    background-color: #f3f4f6;
    border-bottom: 1px solid #e5e7eb;
    border-radius: 0.375rem 0.375rem 0 0;
    padding: 0.75rem;
  }
  .react-datepicker__current-month {
    color: #374151;
    font-weight: 600;
    font-size: 0.875rem;
  }
  .react-datepicker__day-names {
    background-color: #f9fafb;
    border-bottom: 1px solid #e5e7eb;
  }
  .react-datepicker__day-name {
    color: #6b7280;
    font-size: 0.75rem;
    font-weight: 500;
    width: 2rem;
    line-height: 2rem;
  }
  .react-datepicker__day {
    color: #374151;
    font-size: 0.875rem;
    width: 2rem;
    line-height: 2rem;
    margin: 0.125rem;
    border-radius: 0.25rem;
    transition: all 0.2s;
  }
  .react-datepicker__day:hover {
    background-color: #f3f4f6;
    color: #374151;
  }
  .react-datepicker__day--selected {
    background-color: #06b6d4;
    color: #ffffff;
  }
  .react-datepicker__day--selected:hover {
    background-color: #0891b2;
  }
  .react-datepicker__day--keyboard-selected {
    background-color: #06b6d4;
    color: #ffffff;
  }
  .react-datepicker__day--today {
    background-color: #fef3c7;
    color: #92400e;
    font-weight: 600;
  }
  .react-datepicker__day--disabled {
    color: #d1d5db;
    cursor: not-allowed;
  }
  .react-datepicker__day--disabled:hover {
    background-color: transparent;
    color: #d1d5db;
  }
  .react-datepicker__navigation {
    background: none;
    border: none;
    cursor: pointer;
    outline: none;
    top: 0.75rem;
    width: 1.5rem;
    height: 1.5rem;
    border-radius: 0.25rem;
    transition: all 0.2s;
  }
  .react-datepicker__navigation:hover {
    background-color: #f3f4f6;
  }
  .react-datepicker__navigation-icon::before {
    border-color: #6b7280;
    border-width: 2px 2px 0 0;
    width: 6px;
    height: 6px;
  }
  .react-datepicker__triangle {
    display: none;
  }
`;


interface Service {
  id: number;
  name: string;
  phone_id?: string;
  meta_api_key?: string;
}

interface BusinessHours {
  day_of_week: string;
  open_time: string;
  close_time: string;
  id?: string; // Para identificar entradas múltiplas do mesmo dia
}

interface HolidaySchedule {
  date: string;
  start_time: string;
  end_time: string;
}

interface ConfigureBusinessHoursModalProps {
  isOpen: boolean;
  onClose: () => void;
  service: Service | null;
  onConfigured: () => void;
  skipMetaValidation?: boolean;
  preloadedData?: any;
}

const DAYS_OF_WEEK = [
  { value: 'MONDAY', label: 'Segunda-feira' },
  { value: 'TUESDAY', label: 'Terça-feira' },
  { value: 'WEDNESDAY', label: 'Quarta-feira' },
  { value: 'THURSDAY', label: 'Quinta-feira' },
  { value: 'FRIDAY', label: 'Sexta-feira' },
  { value: 'SATURDAY', label: 'Sábado' },
  { value: 'SUNDAY', label: 'Domingo' },
];

const TIMEZONES = [
  // Brazil
  { value: 'America/Sao_Paulo', label: '🇧🇷 São Paulo (GMT-3)' },
  { value: 'America/Rio_Branco', label: '🇧🇷 Rio Branco (GMT-5)' },
  { value: 'America/Manaus', label: '🇧🇷 Manaus (GMT-4)' },
  { value: 'America/Fortaleza', label: '🇧🇷 Fortaleza (GMT-3)' },
  { value: 'America/Recife', label: '🇧🇷 Recife (GMT-3)' },
  { value: 'America/Araguaina', label: '🇧🇷 Araguaína (GMT-3)' },
  { value: 'America/Belem', label: '🇧🇷 Belém (GMT-3)' },
  { value: 'America/Santarem', label: '🇧🇷 Santarém (GMT-3)' },
  { value: 'America/Boa_Vista', label: '🇧🇷 Boa Vista (GMT-4)' },
  { value: 'America/Cuiaba', label: '🇧🇷 Cuiabá (GMT-4)' },
  { value: 'America/Campo_Grande', label: '🇧🇷 Campo Grande (GMT-4)' },
  { value: 'America/Goiania', label: '🇧🇷 Goiânia (GMT-3)' },
  { value: 'America/Brasilia', label: '🇧🇷 Brasília (GMT-3)' },
  { value: 'America/Porto_Velho', label: '🇧🇷 Porto Velho (GMT-4)' },

  // United States
  { value: 'America/New_York', label: '🇺🇸 New York (GMT-5/GMT-4)' },
  { value: 'America/Chicago', label: '🇺🇸 Chicago (GMT-6/GMT-5)' },
  { value: 'America/Denver', label: '🇺🇸 Denver (GMT-7/GMT-6)' },
  { value: 'America/Los_Angeles', label: '🇺🇸 Los Angeles (GMT-8/GMT-7)' },
  { value: 'America/Anchorage', label: '🇺🇸 Anchorage (GMT-9/GMT-8)' },
  { value: 'Pacific/Honolulu', label: '🇺🇸 Honolulu (GMT-10)' },

  // Europe
  { value: 'Europe/London', label: '🇬🇧 London (GMT+0/GMT+1)' },
  { value: 'Europe/Paris', label: '🇫🇷 Paris (GMT+1/GMT+2)' },
  { value: 'Europe/Berlin', label: '🇩🇪 Berlin (GMT+1/GMT+2)' },
  { value: 'Europe/Rome', label: '🇮🇹 Rome (GMT+1/GMT+2)' },
  { value: 'Europe/Madrid', label: '🇪🇸 Madrid (GMT+1/GMT+2)' },
  { value: 'Europe/Amsterdam', label: '🇳🇱 Amsterdam (GMT+1/GMT+2)' },

  // Asia
  { value: 'Asia/Tokyo', label: '🇯🇵 Tokyo (GMT+9)' },
  { value: 'Asia/Shanghai', label: '🇨🇳 Shanghai (GMT+8)' },
  { value: 'Asia/Hong_Kong', label: '🇭🇰 Hong Kong (GMT+8)' },
  { value: 'Asia/Singapore', label: '🇸🇬 Singapore (GMT+8)' },
  { value: 'Asia/Dubai', label: '🇦🇪 Dubai (GMT+4)' },
  { value: 'Asia/Kolkata', label: '🇮🇳 Mumbai (GMT+5:30)' },

  // Other Americas
  { value: 'America/Mexico_City', label: '🇲🇽 Mexico City (GMT-6/GMT-5)' },
  { value: 'America/Bogota', label: '🇨🇴 Bogota (GMT-5)' },
  { value: 'America/Lima', label: '🇵🇪 Lima (GMT-5)' },
  { value: 'America/Santiago', label: '🇨🇱 Santiago (GMT-4/GMT-3)' },
  { value: 'America/Buenos_Aires', label: '🇦🇷 Buenos Aires (GMT-3)' },
  { value: 'America/Montevideo', label: '🇺🇾 Montevideo (GMT-3)' },

  // Africa
  { value: 'Africa/Cairo', label: '🇪🇬 Cairo (GMT+2)' },
  { value: 'Africa/Johannesburg', label: '🇿🇦 Johannesburg (GMT+2)' },
  { value: 'Africa/Lagos', label: '🇳🇬 Lagos (GMT+1)' },
  { value: 'Africa/Nairobi', label: '🇰🇪 Nairobi (GMT+3)' },

  // Oceania
  { value: 'Australia/Sydney', label: '🇦🇺 Sydney (GMT+10/GMT+11)' },
  { value: 'Australia/Melbourne', label: '🇦🇺 Melbourne (GMT+10/GMT+11)' },
  { value: 'Australia/Perth', label: '🇦🇺 Perth (GMT+8)' },
  { value: 'Pacific/Auckland', label: '🇳🇿 Auckland (GMT+12/GMT+13)' },
];

interface ClockTimePickerProps {
  value: string;
  onChange: (time: string) => void;
}

const ClockTimePicker: React.FC<ClockTimePickerProps> = ({ value, onChange }) => {
  const [selectedHour, setSelectedHour] = useState(() => {
    const hour = parseInt(value.split(':')[0]) || 12;
    return hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
  });
  const [selectedMinute, setSelectedMinute] = useState(parseInt(value.split(':')[1]) || 0);
  const [isPM, setIsPM] = useState(() => {
    const hour = parseInt(value.split(':')[0]) || 12;
    return hour >= 12;
  });
  const [mode, setMode] = useState<'hour' | 'minute'>('hour');

  const centerX = 80;
  const centerY = 80;

  const getHourPosition = (hour: number) => {
    const angle = (hour * 30 - 90) * Math.PI / 180; // -90 to start at 12 o'clock
    const radius = 55;
    return {
      left: `${centerX + radius * Math.cos(angle) - 12}px`,
      top: `${centerY + radius * Math.sin(angle) - 12}px`,
    };
  };

  const getSelectedHourAngle = () => {
    return (selectedHour * 30 - 90) * Math.PI / 180;
  };

  const getSelectedHourPosition = () => {
    const angle = getSelectedHourAngle();
    const radius = 55;
    return {
      x: centerX + radius * Math.cos(angle),
      y: centerY + radius * Math.sin(angle),
    };
  };

  const handleHourClick = (hour: number) => {
    setSelectedHour(hour);
    setMode('minute');
  };

  const handleMinuteClick = (minute: number) => {
    setSelectedMinute(minute);
  };

  const handleOK = () => {
    let finalHour = selectedHour;
    if (isPM && selectedHour !== 12) {
      finalHour = selectedHour + 12;
    } else if (!isPM && selectedHour === 12) {
      finalHour = 0;
    }

    const timeString = `${finalHour.toString().padStart(2, '0')}:${selectedMinute.toString().padStart(2, '0')}`;
    onChange(timeString);
  };

  const handleAMPM = (pm: boolean) => {
    setIsPM(pm);
  };

  const handleCancel = () => {
    onChange(value); // Keep original value
  };

  return (
    <div
      className="bg-gray-100 p-4 rounded-lg w-64"
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="text-center text-gray-600 text-xs mb-3 font-medium">
        SELECIONAR HORA
      </div>

      {/* Time Display */}
      <div className="flex items-center justify-center mb-4">
        <div className="flex items-center space-x-1">
          {/* Hour Display */}
          <div className={`px-2 py-2 rounded text-xl font-bold ${
            mode === 'hour' ? 'bg-green-400 text-white' : 'bg-white text-gray-800'
          }`}>
            {selectedHour.toString().padStart(2, '0')}
          </div>

          {/* Colon */}
          <div className="text-xl font-bold text-gray-800">:</div>

          {/* Minute Display */}
          <div className={`px-2 py-2 rounded text-xl font-bold ${
            mode === 'minute' ? 'bg-green-400 text-white' : 'bg-white text-gray-800'
          }`}>
            {selectedMinute.toString().padStart(2, '0')}
          </div>
        </div>

        {/* AM/PM Buttons */}
        <div className="ml-3 flex flex-col space-y-1">
          <button
            type="button"
            onClick={() => handleAMPM(false)}
            className={`px-2 py-1 rounded text-xs font-medium ${
              !isPM ? 'bg-cyan-400 text-white' : 'bg-white text-gray-600'
            }`}
          >
            AM
          </button>
          <button
            type="button"
            onClick={() => handleAMPM(true)}
            className={`px-2 py-1 rounded text-xs font-medium ${
              isPM ? 'bg-cyan-400 text-white' : 'bg-white text-gray-600'
            }`}
          >
            PM
          </button>
        </div>
      </div>

      {/* Clock Face */}
      <div className="relative mb-4 mx-auto" style={{ width: '160px', height: '160px' }}>
        <svg width="160" height="160" className="absolute inset-0">
          {/* Clock circle */}
          <circle
            cx={centerX}
            cy={centerY}
            r="70"
            fill="none"
            stroke="#d1d5db"
            strokeWidth="1"
          />

          {/* Selected hour indicator line */}
          {mode === 'hour' && (
            <line
              x1={centerX}
              y1={centerY}
              x2={getSelectedHourPosition().x}
              y2={getSelectedHourPosition().y}
              stroke="#22c55e"
              strokeWidth="2"
            />
          )}

          {/* Center dot */}
          <circle
            cx={centerX}
            cy={centerY}
            r="3"
            fill="#22c55e"
          />

          {/* Selected hour circle */}
          {mode === 'hour' && (
            <circle
              cx={getSelectedHourPosition().x}
              cy={getSelectedHourPosition().y}
              r="14"
              fill="#22c55e"
              className="cursor-pointer"
            />
          )}
        </svg>

        {/* Hour Numbers */}
        {mode === 'hour' && Array.from({ length: 12 }, (_, i) => {
          const hour = i === 0 ? 12 : i;
          const position = getHourPosition(hour);
          const isSelected = selectedHour === hour;

          return (
            <button
              key={hour}
              type="button"
              onClick={() => handleHourClick(hour)}
              className={`absolute w-6 h-6 flex items-center justify-center text-sm font-medium transition-all ${
                isSelected
                  ? 'text-white z-20'
                  : 'text-gray-700 hover:text-green-600 z-10'
              }`}
              style={position}
            >
              {hour}
            </button>
          );
        })}

        {/* Minute markers (when in minute mode) */}
        {mode === 'minute' && Array.from({ length: 12 }, (_, i) => {
          const minute = i * 5;
          const angle = (minute * 6 - 90) * Math.PI / 180;
          const radius = 55;
          const position = {
            left: `${centerX + radius * Math.cos(angle) - 12}px`,
            top: `${centerY + radius * Math.sin(angle) - 12}px`,
          };
          const isSelected = selectedMinute === minute;

          return (
            <button
              key={minute}
              type="button"
              onClick={() => handleMinuteClick(minute)}
              className={`absolute w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium transition-all z-10 ${
                isSelected
                  ? 'bg-green-500 text-white'
                  : 'text-gray-700 hover:bg-gray-200'
              }`}
              style={position}
            >
              {minute.toString().padStart(2, '0')}
            </button>
          );
        })}
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end space-x-3">
        <button
          onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); handleCancel(); }}
          className="px-4 py-1 text-gray-600 text-sm font-medium hover:text-gray-800 transition-colors"
        >
          CANCELAR
        </button>
        <button
          onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); handleOK(); }}
          className="px-4 py-1 text-green-600 text-sm font-medium hover:text-green-800 transition-colors"
        >
          OK
        </button>
      </div>
    </div>
  );
};

const ConfigureBusinessHoursModal: React.FC<ConfigureBusinessHoursModalProps> = ({
  isOpen,
  onClose,
  service,
  onConfigured,
  skipMetaValidation = false,
  preloadedData,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingConfig, setIsLoadingConfig] = useState(false);
  const [timezoneId, setTimezoneId] = useState('America/Sao_Paulo');
  const [businessHours, setBusinessHours] = useState<BusinessHours[]>([
    { day_of_week: 'MONDAY', open_time: '0600', close_time: '2200', id: 'MONDAY-1' },
    { day_of_week: 'TUESDAY', open_time: '0600', close_time: '2200', id: 'TUESDAY-1' },
    { day_of_week: 'WEDNESDAY', open_time: '0600', close_time: '2200', id: 'WEDNESDAY-1' },
    { day_of_week: 'THURSDAY', open_time: '0600', close_time: '2200', id: 'THURSDAY-1' },
    { day_of_week: 'FRIDAY', open_time: '0600', close_time: '2200', id: 'FRIDAY-1' },
  ]);
  const [holidaySchedule, setHolidaySchedule] = useState<HolidaySchedule[]>([]);
  const [showAddHoliday, setShowAddHoliday] = useState(false);
  const [showTimePicker, setShowTimePicker] = useState<string | null>(null);
  const [newHoliday, setNewHoliday] = useState({
    date: '',
    start_time: '0000',
    end_time: '2359',
  });

  // Close time picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showTimePicker && !(event.target as Element).closest('.time-picker-container')) {
        setShowTimePicker(null);
      }
    };

    if (showTimePicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTimePicker]);


  const loadExistingConfiguration = useCallback(async () => {
    if (!service) return;

    setIsLoadingConfig(true);
    try {
      const response = await api.get(`/services/${service.id}/business-hours`);

      if (response.data?.data?.business_hours) {
        const config = response.data.data.business_hours;

        // Load timezone
        if (config.timezone_id) {
          setTimezoneId(config.timezone_id);
        }

        // Load weekly operating hours
        if (config.weekly_operating_hours && config.weekly_operating_hours.length > 0) {
          const loadedHours = config.weekly_operating_hours.map((hours: any, index: number) => ({
            day_of_week: hours.day_of_week,
            open_time: hours.open_time,
            close_time: hours.close_time,
            id: `${hours.day_of_week}-${index + 1}`,
          }));
          setBusinessHours(loadedHours);
        }

        // Load holiday schedule
        if (config.holiday_schedule && config.holiday_schedule.length > 0) {
          setHolidaySchedule(config.holiday_schedule);
        } else {
          setHolidaySchedule([]); // Ensure empty array
        }
      }
    } catch (error) {
      console.error('Error loading existing business hours configuration:', error);
      // If loading fails, keep default values
    } finally {
      setIsLoadingConfig(false);
    }
  }, [service]);

  const loadPreloadedConfiguration = useCallback((data: any) => {
    if (!data) return;

    try {
      // Load timezone
      if (data.timezone_id) {
        setTimezoneId(data.timezone_id);
      }

      // Load weekly operating hours
      if (data.weekly_operating_hours && data.weekly_operating_hours.length > 0) {
        const loadedHours = data.weekly_operating_hours.map((hours: any, index: number) => ({
          day_of_week: hours.day_of_week,
          open_time: hours.open_time,
          close_time: hours.close_time,
          id: `${hours.day_of_week}-${index + 1}`,
        }));
        setBusinessHours(loadedHours);
      }

      // Load holiday schedule
      if (data.holiday_schedule && data.holiday_schedule.length > 0) {
        setHolidaySchedule(data.holiday_schedule);
      } else {
        setHolidaySchedule([]); // Ensure empty array
      }

      // Set loading to false immediately since data is already loaded
      setIsLoadingConfig(false);
    } catch (error) {
      console.error('Error loading preloaded business hours configuration:', error);
      // If loading fails, keep default values
      setIsLoadingConfig(false);
    }
  }, []);

  // Load existing configuration when modal opens
  useEffect(() => {
    if (isOpen && service) {
      if (preloadedData) {
        // Use preloaded data if available
        loadPreloadedConfiguration(preloadedData);
      } else {
        // Fallback to loading from API
        loadExistingConfiguration();
      }
    }
  }, [isOpen, service, preloadedData, loadExistingConfiguration, loadPreloadedConfiguration]);

  const addHoliday = () => {
    if (!newHoliday.date) {
      import('react-hot-toast').then(({ default: toast }) => {
        toast.error('Selecione uma data para o feriado');
      });
      return;
    }
  
    // Normalize "HH:MM" -> "HHMM" for comparison/payload
    const normalize = (t: string) => {
      if (!t) return t;
      if (/^([01]\d|2[0-3]):([0-5]\d)$/.test(t)) return `${t.slice(0, 2)}${t.slice(3, 5)}`;
      return t; // already HHMM
    };
  
    const candidate = {
      date: newHoliday.date,
      start_time: normalize(newHoliday.start_time),
      end_time: normalize(newHoliday.end_time),
    };
  
    // Meta rules (holiday_schedule):
    // - date must be YYYY-MM-DD
    // - open_time/close_time must be HHMM (4 chars), open_time < close_time
    // - Max 20 overrides total
    // - For the same date, max 2 intervals and no overlaps
  
    // Validate date format
    if (!/^\d{4}-\d{2}-\d{2}$/.test(candidate.date)) {
      import('react-hot-toast').then(({ default: toast }) => {
        toast.error('Data inválida. Use o formato YYYY-MM-DD.');
      });
      return;
    }
  
    // Validate HHMM format
    const hhmm = /^([01]\d|2[0-3])([0-5]\d)$/;
    if (!hhmm.test(candidate.start_time) || !hhmm.test(candidate.end_time)) {
      import('react-hot-toast').then(({ default: toast }) => {
        toast.error('Horários inválidos. Use HHMM (ex.: 0830).');
      });
      return;
    }
  
    const toMinutes = (t: string) => parseInt(t.slice(0, 2), 10) * 60 + parseInt(t.slice(2, 4), 10);
    const cStart = toMinutes(candidate.start_time);
    const cEnd = toMinutes(candidate.end_time);
    if (cStart >= cEnd) {
      import('react-hot-toast').then(({ default: toast }) => {
        toast.error('Horário inicial deve ser anterior ao horário final.');
      });
      return;
    }
  
    // Max 20 overrides
    if (holidaySchedule.length >= 20) {
      import('react-hot-toast').then(({ default: toast }) => {
        toast.error('Limite de 20 feriados atingido.');
      });
      return;
    }
  
    // Same date validations
    const sameDate = holidaySchedule.filter((h) => h.date === candidate.date);
  
    // Max 2 per date
    if (sameDate.length >= 2) {
      import('react-hot-toast').then(({ default: toast }) => {
        toast.error('No máximo 2 intervalos permitidos para a mesma data.');
      });
      return;
    }
  
    // No exact duplicates (same date and same range)
    const existsExact = sameDate.some(
      (h) => h.start_time === candidate.start_time && h.end_time === candidate.end_time
    );
    if (existsExact) {
      import('react-hot-toast').then(({ default: toast }) => {
        toast.error('Este feriado já foi adicionado com os mesmos horários.');
      });
      return;
    }
  
    // No overlaps on same date
    const overlaps = sameDate.some((h) => {
      const s = toMinutes(normalize(h.start_time));
      const e = toMinutes(normalize(h.end_time));
      return cStart < e && s < cEnd;
    });
    if (overlaps) {
      import('react-hot-toast').then(({ default: toast }) => {
        toast.error('Intervalo de feriado sobreposto nesta data.');
      });
      return;
    }
  
    // OK to add
    setHolidaySchedule([...holidaySchedule, candidate]);
    setNewHoliday({ date: '', start_time: '0000', end_time: '2359' });
    setShowAddHoliday(false);
  
  };

  const removeHoliday = (index: number) => {
    setHolidaySchedule(holidaySchedule.filter((_, i) => i !== index));
  };

  const resetForm = () => {
    setTimezoneId('America/Sao_Paulo');
    // Start with common business days (Monday to Friday) and let users add/remove as needed
    setBusinessHours([
      { day_of_week: 'MONDAY', open_time: '0600', close_time: '2200', id: 'MONDAY-1' },
      { day_of_week: 'TUESDAY', open_time: '0600', close_time: '2200', id: 'TUESDAY-1' },
      { day_of_week: 'WEDNESDAY', open_time: '0600', close_time: '2200', id: 'WEDNESDAY-1' },
      { day_of_week: 'THURSDAY', open_time: '0600', close_time: '2200', id: 'THURSDAY-1' },
      { day_of_week: 'FRIDAY', open_time: '0600', close_time: '2200', id: 'FRIDAY-1' },
    ]);
    setHolidaySchedule([]);
    setNewHoliday({ date: '', start_time: '0000', end_time: '2359' });
    setShowAddHoliday(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!service) return;

    // Check for incomplete holiday form
    const hasIncompleteHoliday = newHoliday.date ||
      (newHoliday.start_time !== '0000' && newHoliday.start_time !== '') ||
      (newHoliday.end_time !== '2359' && newHoliday.end_time !== '');

    if (hasIncompleteHoliday) {
      import('react-hot-toast').then(({ default: toast }) => {
        toast.error('Você tem um feriado preenchido mas não adicionado. Clique em "Adicionar ao Formulário" primeiro ou limpe os campos.');
      });
      return;
    }

    // Validate all business hours (only for days that have hours configured)
    const dayGroups: { [key: string]: BusinessHours[] } = {};

    // Group hours by day
    businessHours.forEach(hours => {
      if (!dayGroups[hours.day_of_week]) {
        dayGroups[hours.day_of_week] = [];
      }
      dayGroups[hours.day_of_week].push(hours);
    });

    // Validate each day that has hours configured
    for (const [day, entries] of Object.entries(dayGroups)) {
      // Check max 2 entries per day
      if (entries.length > 2) {
        import('react-hot-toast').then(({ default: toast }) => {
          toast.error(`Máximo de 2 horários permitidos por dia. ${DAYS_OF_WEEK.find(d => d.value === day)?.label} tem ${entries.length} entradas`);
        });
        return;
      }

      // Validate each entry and check for overlaps
      for (let i = 0; i < entries.length; i++) {
        const hours = entries[i];
        if (hours.open_time && hours.close_time && hours.open_time.length === 4 && hours.close_time.length === 4) {
          const openMinutes = parseInt(hours.open_time.substring(0, 2)) * 60 + parseInt(hours.open_time.substring(2, 4));
          const closeMinutes = parseInt(hours.close_time.substring(0, 2)) * 60 + parseInt(hours.close_time.substring(2, 4));

          if (closeMinutes <= openMinutes) {
            import('react-hot-toast').then(({ default: toast }) => {
              toast.error(`Horário de fechamento deve ser posterior ao horário de abertura para ${DAYS_OF_WEEK.find(d => d.value === hours.day_of_week)?.label}`);
            });
            return;
          }

          // Check for overlaps with other entries of the same day
          for (let j = i + 1; j < entries.length; j++) {
            const otherHours = entries[j];
            if (otherHours.open_time && otherHours.close_time && otherHours.open_time.length === 4 && otherHours.close_time.length === 4) {
              const otherOpenMinutes = parseInt(otherHours.open_time.substring(0, 2)) * 60 + parseInt(otherHours.open_time.substring(2, 4));
              const otherCloseMinutes = parseInt(otherHours.close_time.substring(0, 2)) * 60 + parseInt(otherHours.close_time.substring(2, 4));

              const hasOverlap = !(closeMinutes <= otherOpenMinutes || otherCloseMinutes <= openMinutes);
              if (hasOverlap) {
                import('react-hot-toast').then(({ default: toast }) => {
                  toast.error(`Horários sobrepostos para ${DAYS_OF_WEEK.find(d => d.value === day)?.label}. Os intervalos não podem se sobrepor`);
                });
                return;
              }
            }
          }
        }
      }
    }

    setIsLoading(true);
    try {
      const weeklyOperatingHours = businessHours.map(hours => ({
        day_of_week: hours.day_of_week,
        open_time: hours.open_time,
        close_time: hours.close_time,
      }));

      // Always send holiday_schedule (Meta clears it if missing)
      const holidayForPayload = holidaySchedule.map(h => ({
        date: h.date,
        open_time: h.start_time,
        close_time: h.end_time,
      }));

      const payload: any = {
        timezone_id: timezoneId,
        weekly_operating_hours: weeklyOperatingHours,
        holiday_schedule: holidayForPayload,
      };

      await api.post(`/services/${service.id}/configure-business-hours`, payload);

      // Show success message
      import('react-hot-toast').then(({ default: toast }) => {
        toast.success('Horário comercial configurado com sucesso!');
      });

      onConfigured();
      onClose();
      resetForm();
    } catch (error: any) {
      console.error('Business hours configuration error:', error);

      const errorMessage = error.response?.data?.error || 'Erro ao configurar horário comercial';

      import('react-hot-toast').then(({ default: toast }) => {
        toast.error(errorMessage);
      });
    } finally {
      setIsLoading(false);
    }
  };

  const updateBusinessHours = (id: string, field: keyof BusinessHours, value: string) => {
    const updatedHours = businessHours.map(hours =>
      hours.id === id ? { ...hours, [field]: value } : hours
    );
    setBusinessHours(updatedHours);
  };

  const formatTimeForDisplay = (time: string) => {
    if (time.length !== 4) return time;
    const hours = time.substring(0, 2);
    const minutes = time.substring(2, 4);
    return `${hours}:${minutes}`;
  };

  const formatTimeForPicker = (time: string) => {
    if (!time || time.length === 0) return '';
    if (time.length === 4) {
      const hours = time.substring(0, 2);
      const minutes = time.substring(2, 4);
      return `${hours}:${minutes}`;
    }
    // For partial input, don't add colons to avoid multiple colons
    return time;
  };

  // Simple formatter that handles edge cases better
  const formatTimeForInput = (time: string) => {
    if (!time) return '';
    // If it's already formatted with colon, return as is
    if (time.includes(':')) return time;
    // If it's 4 digits, format it
    if (time.length === 4) {
      return `${time.substring(0, 2)}:${time.substring(2, 4)}`;
    }
    // Otherwise return as is
    return time;
  };

  const formatTimeForBackend = (time: string) => {
    if (!time || time === '--:--') return '';
    const [hours, minutes] = time.split(':');
    return `${hours}${minutes}`;
  };

  const handleTimeChange = (id: string, field: 'open_time' | 'close_time', value: string | null) => {
    const formattedTime = value ? formatTimeForBackend(value) : '';
    updateBusinessHours(id, field, formattedTime);

    // Validate that close time is after open time
    const index = businessHours.findIndex(h => h.id === id);
    if (index !== -1) {
      validateBusinessHours(index);
    }
  };

  const handleDirectTimeInput = (id: string, field: 'open_time' | 'close_time', value: string) => {
    // Get current value
    const currentEntry = businessHours.find(h => h.id === id);
    if (!currentEntry) return;

    // If user types a single digit, always clear the field to start fresh
    if (/^\d$/.test(value)) {
      updateBusinessHours(id, field, value);
      return;
    }

    // Allow typing and auto-format
    let cleanValue = value.replace(/[^0-9]/g, '');

    if (cleanValue.length >= 4) {
      cleanValue = cleanValue.substring(0, 4);
    }

    // Auto-add colon after 2 digits
    if (cleanValue.length === 2 && value.length === 2) {
      cleanValue = cleanValue + ':';
    }

    // Validate hour range (00-23)
    if (cleanValue.length >= 2) {
      const hour = parseInt(cleanValue.substring(0, 2));
      if (hour > 23) {
        cleanValue = '23' + cleanValue.substring(2);
      }
    }

    // Validate minute range (00-59)
    if (cleanValue.length >= 4) {
      const minute = parseInt(cleanValue.substring(2, 4));
      if (minute > 59) {
        cleanValue = cleanValue.substring(0, 2) + '59';
      }
    }

    updateBusinessHours(id, field, cleanValue);
  };

  // Ultra-simple version - just replace the value
  const handleDirectTimeInputSimple = (id: string, field: 'open_time' | 'close_time', value: string) => {
    // Always update the value directly without any formatting
    // This ensures the raw value is stored and formatted only for display
    updateBusinessHours(id, field, value);

    // Validate that close time is after open time
    const index = businessHours.findIndex(h => h.id === id);
    if (index !== -1) {
      validateBusinessHours(index);
    }
  };

  // Validate that close time is after open time for a specific entry
  const validateBusinessHours = (index: number) => {
    const hours = businessHours[index];
    if (hours.open_time && hours.close_time && hours.open_time.length === 4 && hours.close_time.length === 4) {
      const openMinutes = parseInt(hours.open_time.substring(0, 2)) * 60 + parseInt(hours.open_time.substring(2, 4));
      const closeMinutes = parseInt(hours.close_time.substring(0, 2)) * 60 + parseInt(hours.close_time.substring(2, 4));

      if (closeMinutes <= openMinutes) {
        // Show error message
        import('react-hot-toast').then(({ default: toast }) => {
          toast.error(`Horário de fechamento deve ser posterior ao horário de abertura para ${DAYS_OF_WEEK.find(day => day.value === hours.day_of_week)?.label}`);
        });
        return false;
      }
    }
    return true;
  };

  // Check if a specific entry's hours are valid (basic validation)
  const isValidBusinessHours = (index: number) => {
    const hours = businessHours[index];
    if (!hours.open_time || !hours.close_time || hours.open_time.length !== 4 || hours.close_time.length !== 4) {
      return true; // Not enough data to validate
    }

    const openMinutes = parseInt(hours.open_time.substring(0, 2)) * 60 + parseInt(hours.open_time.substring(2, 4));
    const closeMinutes = parseInt(hours.close_time.substring(0, 2)) * 60 + parseInt(hours.close_time.substring(2, 4));

    return closeMinutes > openMinutes;
  };

  if (!isOpen) return null;

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: datePickerStyles }} />
      <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4">
        <div className="w-full max-w-4xl max-h-[85vh] bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-2xl border border-gray-200 overflow-hidden flex flex-col">
        <div className="overflow-y-auto p-5 flex-1">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-1">
                {isLoadingConfig ? 'Carregando Configuração...' : 'Configurar Horário Comercial'}
              </h3>
              <p className="text-gray-600 text-sm">
                {isLoadingConfig
                  ? (skipMetaValidation
                      ? 'Meta API validada ✓ - Buscando configuração atual'
                      : 'Buscando configuração atual da Meta API'
                    )
                  : 'Configure os horários de funcionamento do WhatsApp Business'
                }
              </p>
              {skipMetaValidation && !isLoadingConfig && (
                <div className="mt-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  <CheckCircleIcon className="h-3 w-3 mr-1" />
                  Meta API OK
                </div>
              )}
            </div>
            <button
              onClick={onClose}
              disabled={isLoadingConfig}
              className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-2 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <XCircleIcon className="h-6 w-6" />
            </button>
          </div>


          <form onSubmit={handleSubmit} className="space-y-5">
            {isLoadingConfig && (
              <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded-2xl">
                <div className="flex items-center space-x-3">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-cyan-600"></div>
                  <span className="text-sm text-gray-600">Carregando configuração...</span>
                </div>
              </div>
            )}
            {/* Timezone Selection */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Fuso Horário
              </label>
              <select
                value={timezoneId}
                onChange={(e) => setTimezoneId(e.target.value)}
                disabled={isLoadingConfig}
                className="block w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all bg-gray-50 focus:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                required
              >
                {TIMEZONES.map((tz) => (
                  <option key={tz.value} value={tz.value}>
                    {tz.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Business Hours Configuration */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <label className="block text-sm font-semibold text-gray-700">
                  Horários de Funcionamento
                </label>
              </div>

              {/* Info box about day management */}
              <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-xs text-blue-700">
                  <strong>Dica:</strong> Configure apenas os dias em que seu negócio funciona.
                  Use "Remover Dia" para excluir dias sem expediente e "Adicionar Dia" para incluí-los novamente.
                  Cada dia pode ter até 2 horários diferentes (ex.: manhã e tarde).
                </p>
              </div>
              <div className="space-y-4">
                {DAYS_OF_WEEK.map((day) => {
                  const dayHours = businessHours.filter(h => h.day_of_week === day.value);
                  const hasInvalidHours = dayHours.some((_, idx) => {
                    const globalIndex = businessHours.findIndex(h => h.id === dayHours[idx].id);
                    return globalIndex !== -1 && !isValidBusinessHours(globalIndex);
                  });

                  // If day has no hours, show add day option
                  if (dayHours.length === 0) {
                    return (
                      <div key={day.value} className="p-4 rounded-lg border border-gray-200 bg-gray-25">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-medium text-gray-500">{day.label}</span>
                            <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded">Sem expediente</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              const newId = `${day.value}-1`;
                              const newEntry: BusinessHours = {
                                day_of_week: day.value,
                                open_time: '0600',
                                close_time: '2200',
                                id: newId
                              };
                              setBusinessHours([...businessHours, newEntry]);
                            }}
                            disabled={isLoadingConfig}
                            className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-green-700 bg-green-100 hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Adicionar expediente"
                          >
                            <PlusIcon className="h-3 w-3 mr-1" />
                            Adicionar Dia
                          </button>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div key={day.value} className={`p-4 rounded-lg border ${hasInvalidHours ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}`}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-medium text-gray-700">{day.label}</span>
                          {hasInvalidHours && <XCircleIcon className="h-4 w-4 text-red-500" />}
                        </div>
                        <div className="flex items-center space-x-2">
                          {dayHours.length < 2 && (
                            <button
                              type="button"
                              onClick={() => {
                                const newId = `${day.value}-${dayHours.length + 1}`;
                                const newEntry: BusinessHours = {
                                  day_of_week: day.value,
                                  open_time: '0600',
                                  close_time: '2200',
                                  id: newId
                                };
                                setBusinessHours([...businessHours, newEntry]);
                              }}
                              disabled={isLoadingConfig}
                              className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-cyan-700 bg-cyan-100 hover:bg-cyan-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Adicionar horário"
                            >
                              <PlusIcon className="h-3 w-3 mr-1" />
                              Adicionar
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => {
                              // Remove all hours for this day
                              setBusinessHours(businessHours.filter(h => h.day_of_week !== day.value));
                            }}
                            disabled={isLoadingConfig}
                            className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Remover dia"
                          >
                            <TrashIcon className="h-3 w-3 mr-1" />
                            Remover Dia
                          </button>
                        </div>
                      </div>

                      <div className="space-y-2">
                        {dayHours.map((hours, dayIndex) => {
                          const globalIndex = businessHours.findIndex(h => h.id === hours.id);
                          const isValid = globalIndex !== -1 ? isValidBusinessHours(globalIndex) : true;
                          const safeId = hours.id || `fallback-${day.value}-${dayIndex}`;

                          return (
                            <div key={safeId} className="flex items-center space-x-3">
                              <div className="flex items-center space-x-2 flex-1">
                                <ClockIcon className="h-4 w-4 text-gray-400" />
                                <div className="relative">
                                  <input
                                    type="text"
                                    value={formatTimeForInput(hours.open_time)}
                                    onChange={(e) => handleDirectTimeInputSimple(safeId, 'open_time', e.target.value)}
                                    disabled={isLoadingConfig}
                                    className={`w-24 px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed ${
                                      !isValid ? 'border-red-500 bg-red-50 focus:border-red-500' : 'border-gray-300 focus:border-cyan-500'
                                    }`}
                                    placeholder="00:00"
                                    maxLength={5}
                                    required
                                  />
                                  <ClockIcon
                                    className="absolute right-2 top-2.5 h-4 w-4 text-gray-400 cursor-pointer hover:text-gray-600"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setShowTimePicker(showTimePicker === `open-${safeId}` ? null : `open-${safeId}`);
                                    }}
                                  />

                                  {showTimePicker === `open-${safeId}` && (
                                    <div className="time-picker-container absolute z-10 mt-1 bg-white border border-gray-300 rounded-lg shadow-xl">
                                      <ClockTimePicker
                                        value={hours.open_time ? formatTimeForPicker(hours.open_time) : '00:00'}
                                        onChange={(time) => {
                                          handleTimeChange(safeId, 'open_time', time);
                                          setShowTimePicker(null);
                                        }}
                                      />
                                    </div>
                                  )}
                                </div>
                                <span className="text-sm text-gray-500">às</span>
                                <div className="relative">
                                  <input
                                    type="text"
                                    value={formatTimeForInput(hours.close_time)}
                                    onChange={(e) => handleDirectTimeInputSimple(safeId, 'close_time', e.target.value)}
                                    disabled={isLoadingConfig}
                                    className={`w-24 px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed ${
                                      !isValid ? 'border-red-500 bg-red-50 focus:border-red-500' : 'border-gray-300 focus:border-cyan-500'
                                    }`}
                                    placeholder="23:59"
                                    maxLength={5}
                                    required
                                  />
                                  <ClockIcon
                                    className="absolute right-2 top-2.5 h-4 w-4 text-gray-400 cursor-pointer hover:text-gray-600"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setShowTimePicker(showTimePicker === `close-${safeId}` ? null : `close-${safeId}`);
                                    }}
                                  />

                                  {showTimePicker === `close-${safeId}` && (
                                    <div className="time-picker-container absolute z-10 mt-1 bg-white border border-gray-300 rounded-lg shadow-xl">
                                      <ClockTimePicker
                                        value={hours.close_time ? formatTimeForPicker(hours.close_time) : '00:00'}
                                        onChange={(time) => {
                                          handleTimeChange(safeId, 'close_time', time);
                                          setShowTimePicker(null);
                                        }}
                                      />
                                    </div>
                                  )}
                                </div>

                                <div className="text-xs text-gray-500">
                                  {hours.open_time && hours.close_time && hours.open_time.length === 4 && hours.close_time.length === 4
                                    ? `${formatTimeForDisplay(hours.open_time)} - ${formatTimeForDisplay(hours.close_time)}`
                                    : 'Selecione os horários'}
                                </div>
                              </div>

                              {dayHours.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setBusinessHours(businessHours.filter(h => h.id !== safeId));
                                  }}
                                  disabled={isLoadingConfig}
                                  className="inline-flex items-center justify-center p-1 border border-transparent text-xs font-medium rounded text-red-600 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                  title="Remover horário"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>

            </div>

            {/* Holiday Schedule Configuration */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <label className="block text-sm font-semibold text-gray-700">
                  Feriados e Datas Especiais
                </label>
                <button
                  type="button"
                  onClick={() => setShowAddHoliday(!showAddHoliday)}
                  disabled={isLoadingConfig}
                  className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-cyan-700 bg-cyan-100 hover:bg-cyan-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <PlusIcon className="h-3 w-3 mr-1" />
                  Adicionar Feriado
                </button>
              </div>

              {/* Add Holiday Form */}
              {showAddHoliday && (
                <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Data
                      </label>
                      <DatePicker
                        selected={newHoliday.date ? new Date(newHoliday.date + 'T00:00:00') : null}
                        onChange={(date) => {
                          if (date) {
                            // Use local date to avoid timezone conversion issues
                            const year = date.getFullYear();
                            const month = String(date.getMonth() + 1).padStart(2, '0');
                            const day = String(date.getDate()).padStart(2, '0');
                            const localDateString = `${year}-${month}-${day}`;
                            setNewHoliday({
                              ...newHoliday,
                              date: localDateString
                            });
                          } else {
                            setNewHoliday({
                              ...newHoliday,
                              date: ''
                            });
                          }
                        }}
                        dateFormat="dd/MM/yyyy"
                        placeholderText="Selecione a data"
                        minDate={new Date()}
                        className="block w-full"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Horário Inicial
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          value={newHoliday.start_time ? formatTimeForPicker(newHoliday.start_time) : '00:00'}
                          onChange={(e) => {
                            const value = e.target.value;
                            const currentValue = newHoliday.start_time;

                            // If user types a single digit, always clear the field to start fresh
                            if (/^\d$/.test(value)) {
                              setNewHoliday({
                                ...newHoliday,
                                start_time: value
                              });
                              return;
                            }

                            let cleanValue = value.replace(/[^0-9]/g, '');

                            if (cleanValue.length >= 4) {
                              cleanValue = cleanValue.substring(0, 4);
                            }

                            if (cleanValue.length === 2 && value.length === 2) {
                              cleanValue = cleanValue + ':';
                            }

                            if (cleanValue.length >= 2) {
                              const hour = parseInt(cleanValue.substring(0, 2));
                              if (hour > 23) {
                                cleanValue = '23' + cleanValue.substring(2);
                              }
                            }

                            if (cleanValue.length >= 4) {
                              const minute = parseInt(cleanValue.substring(2, 4));
                              if (minute > 59) {
                                cleanValue = cleanValue.substring(0, 2) + '59';
                              }
                            }

                            setNewHoliday({
                              ...newHoliday,
                              start_time: cleanValue
                            });
                          }}
                          className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                          placeholder="00:00"
                          maxLength={5}
                        />
                        <ClockIcon
                          className="absolute right-2 top-2.5 h-4 w-4 text-gray-400 cursor-pointer hover:text-gray-600"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowTimePicker(showTimePicker === 'holiday-start' ? null : 'holiday-start');
                          }}
                        />

                        {showTimePicker === 'holiday-start' && (
                          <div
                            className="time-picker-container absolute z-10 mt-1 bg-white border border-gray-300 rounded-lg shadow-xl"
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <ClockTimePicker
                              value={newHoliday.start_time ? formatTimeForPicker(newHoliday.start_time) : '00:00'}
                              onChange={(time) => {
                                setNewHoliday({
                                  ...newHoliday,
                                  start_time: formatTimeForBackend(time)
                                });
                                setShowTimePicker(null);
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Horário Final
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          value={newHoliday.end_time ? formatTimeForPicker(newHoliday.end_time) : '23:59'}
                          onChange={(e) => {
                            const value = e.target.value;
                            const currentValue = newHoliday.end_time;

                            // If user types a single digit, always clear the field to start fresh
                            if (/^\d$/.test(value)) {
                              setNewHoliday({
                                ...newHoliday,
                                end_time: value
                              });
                              return;
                            }

                            let cleanValue = value.replace(/[^0-9]/g, '');

                            if (cleanValue.length >= 4) {
                              cleanValue = cleanValue.substring(0, 4);
                            }

                            if (cleanValue.length === 2 && value.length === 2) {
                              cleanValue = cleanValue + ':';
                            }

                            if (cleanValue.length >= 2) {
                              const hour = parseInt(cleanValue.substring(0, 2));
                              if (hour > 23) {
                                cleanValue = '23' + cleanValue.substring(2);
                              }
                            }

                            if (cleanValue.length >= 4) {
                              const minute = parseInt(cleanValue.substring(2, 4));
                              if (minute > 59) {
                                cleanValue = cleanValue.substring(0, 2) + '59';
                              }
                            }

                            setNewHoliday({
                              ...newHoliday,
                              end_time: cleanValue
                            });
                          }}
                          className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                          placeholder="23:59"
                          maxLength={5}
                        />
                        <ClockIcon
                          className="absolute right-2 top-2.5 h-4 w-4 text-gray-400 cursor-pointer hover:text-gray-600"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowTimePicker(showTimePicker === 'holiday-end' ? null : 'holiday-end');
                          }}
                        />

                        {showTimePicker === 'holiday-end' && (
                          <div
                            className="time-picker-container absolute z-10 mt-1 bg-white border border-gray-300 rounded-lg shadow-xl"
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <ClockTimePicker
                              value={newHoliday.end_time ? formatTimeForPicker(newHoliday.end_time) : '23:59'}
                              onChange={(time) => {
                                setNewHoliday({
                                  ...newHoliday,
                                  end_time: formatTimeForBackend(time)
                                });
                                setShowTimePicker(null);
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-end space-x-2 mt-3">
                    <button
                      type="button"
                      onClick={() => {
                        setShowAddHoliday(false);
                        setNewHoliday({ date: '', start_time: '0000', end_time: '2359' });
                      }}
                      className="px-3 py-1 border border-gray-300 rounded-md text-xs font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        addHoliday();
                      }}
                      className="px-3 py-1 border border-transparent rounded-md text-xs font-medium text-white bg-cyan-600 hover:bg-cyan-700"
                    >
                      Adicionar ao Formulário
                    </button>
                  </div>
                </div>
              )}

              {/* Holiday List */}
              {holidaySchedule.length === 0 ? (
                <div className="text-center py-6 border-2 border-dashed border-gray-300 rounded-lg">
                  <CalendarIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">Nenhum feriado configurado</p>
                  <p className="text-xs text-gray-400 mt-1">
                    Adicione feriados especiais para o seu negócio
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {holidaySchedule
                    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
                    .map((holiday, index) => {
                      return (
                        <div key={index} className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200">
                        <div className="flex items-center space-x-3">
                          <CalendarIcon className="h-5 w-5 text-red-500" />
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {new Date(holiday.date).toLocaleDateString('pt-BR', {
                                weekday: 'long',
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </p>
                            <p className="text-xs text-gray-600">
                              {holiday.start_time === '0000' && holiday.end_time === '2359'
                                ? 'Dia todo'
                                : `${formatTimeForDisplay(holiday.start_time)} - ${formatTimeForDisplay(holiday.end_time)}`
                              }
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeHoliday(index)}
                          disabled={isLoadingConfig}
                          className="inline-flex items-center justify-center p-1 border border-transparent text-xs font-medium rounded text-red-600 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Remover feriado"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    );
                    })}
                </div>
              )}

              <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-xs text-blue-700">
                  <strong>Dica:</strong> Configure feriados e datas especiais onde seu negócio não estará disponível.
                  Os horários padrão não se aplicam nestas datas.
                </p>                
              </div>
            </div>

            <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-3 border-2 border-gray-300 rounded-xl text-sm font-semibold text-gray-700 hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 transition-all"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isLoading || isLoadingConfig}
                className="px-6 py-3 border border-transparent rounded-xl shadow-lg text-sm font-semibold text-white bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:scale-105 flex items-center"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Configurando...
                  </>
                ) : isLoadingConfig ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Carregando...
                  </>
                ) : (
                  'Salvar Configuração'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </>
  );
};

export default ConfigureBusinessHoursModal;
